import { forwardRef } from 'react';
import { Flex, Box } from '@chakra-ui/react';
import { LuPlus, LuGripVertical } from 'react-icons/lu';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import type { DragEndEvent } from '@dnd-kit/core';
import type { MenuContentCategory } from './types';
import type { MenuTheme } from '../../api/menus';
import SortableRow from './SortableRow';
import InlineEdit from './InlineEdit';

interface CategoryTabsProps {
  categories: MenuContentCategory[];
  activeTab: string | null;
  theme: MenuTheme;
  hoverBg: string;
  isEditMode: boolean;
  onTabClick: (id: string) => void;
  onAddCategory?: () => void;
  onReorderCategories?: (event: DragEndEvent) => void;
  onQuickEditCategory?: (categoryId: string, updates: { name?: string }) => void;
}

const CategoryTabs = forwardRef<HTMLDivElement, CategoryTabsProps>(({
  categories,
  activeTab,
  theme: mt,
  hoverBg,
  isEditMode,
  onTabClick,
  onAddCategory,
  onReorderCategories,
  onQuickEditCategory,
}, ref) => {
  const { t } = useTranslation();
  const isQuickEditMode = !!onQuickEditCategory;

  return (
    <Flex
      ref={ref}
      px="20px"
      pb="8px"
      pt="8px"
      gap="12px"
      overflowX="auto"
      borderBottomWidth="1px"
      borderColor={mt.border_color}
      alignItems="center"
      position="sticky"
      top="0"
      zIndex={10}
      backgroundColor={mt.background_color}
      css={{
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      {isEditMode && onReorderCategories ? (
        <DndContext collisionDetection={closestCenter} onDragEnd={onReorderCategories}>
          <SortableContext items={categories.map(c => c.id)} strategy={horizontalListSortingStrategy}>
            {categories.map((cat, idx) => {
              const isActive = activeTab ? activeTab === cat.id : idx === 0;
              return (
                <SortableRow key={cat.id} id={cat.id}>
                  {({ dragRef, dragStyle, dragHandleProps }) => (
                    <Flex
                      ref={dragRef}
                      style={dragStyle}
                      alignItems="center"
                      gap="4px"
                      pt="6px"
                      pb="10px"
                      px="8px"
                      borderBottomWidth="2px"
                      borderBottomColor={isActive ? mt.accent_color : 'transparent'}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderRightWidth="1px"
                      borderTopColor="transparent"
                      borderLeftColor="transparent"
                      borderRightColor="transparent"
                      borderStyle="solid"
                      borderRadius="4px 4px 0 0"
                      flexShrink={0}
                      transition="border-color 0.15s, background-color 0.15s"
                      _hover={{
                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                        borderTopColor: 'rgba(59, 130, 246, 0.5)',
                        borderLeftColor: 'rgba(59, 130, 246, 0.5)',
                        borderRightColor: 'rgba(59, 130, 246, 0.5)',
                      }}
                    >
                      <Flex {...dragHandleProps} alignItems="center" style={{ cursor: 'grab' }} onClick={(e) => e.stopPropagation()}>
                        <LuGripVertical style={{ width: 12, height: 12, color: mt.secondary_text_color }} />
                      </Flex>
                      <Flex
                        alignItems="center"
                        gap="4px"
                        whiteSpace="nowrap"
                        cursor="pointer"
                        onClick={() => onTabClick(cat.id)}
                      >
                        <Box fontSize="15px" fontWeight={isActive ? '600' : '400'} color={isActive ? mt.accent_color : mt.secondary_text_color}>
                          {cat.name}
                        </Box>
                        {cat.is_visible === false && (
                          <Box fontSize="9px" fontWeight="500" color="#999999" backgroundColor="#F0F0F0" px="4px" py="1px" borderRadius="3px">
                            {t('menus.hidden')}
                          </Box>
                        )}
                      </Flex>
                    </Flex>
                  )}
                </SortableRow>
              );
            })}
          </SortableContext>
        </DndContext>
      ) : isQuickEditMode ? (
        categories.map((cat, idx) => {
          const isActive = activeTab ? activeTab === cat.id : idx === 0;
          return (
            <Box
              key={cat.id}
              data-category-tab={cat.id}
              pt="6px"
              pb="10px"
              borderBottomWidth="2px"
              borderColor={isActive ? mt.accent_color : 'transparent'}
              flexShrink={0}
            >
              <InlineEdit
                value={cat.name}
                onSave={(name) => onQuickEditCategory(cat.id, { name })}
                fontSize="15px"
                fontWeight={isActive ? '600' : '400'}
                color={isActive ? mt.accent_color : mt.secondary_text_color}
              />
            </Box>
          );
        })
      ) : (
        categories.map((cat, idx) => {
          const isActive = activeTab ? activeTab === cat.id : idx === 0;
          return (
            <Box
              key={cat.id}
              data-category-tab={cat.id}
              fontSize="15px"
              fontWeight={isActive ? '600' : '400'}
              color={isActive ? mt.accent_color : mt.secondary_text_color}
              pt="6px"
              pb="10px"
              px="8px"
              borderBottomWidth="2px"
              borderColor={isActive ? mt.accent_color : 'transparent'}
              whiteSpace="nowrap"
              cursor="pointer"
              flexShrink={0}
              onClick={() => onTabClick(cat.id)}
            >
              {cat.name}
            </Box>
          );
        })
      )}
      {isEditMode && onAddCategory && (
        <Flex
          alignItems="center"
          gap="4px"
          mb="8px"
          px="10px"
          py="4px"
          cursor="pointer"
          flexShrink={0}
          color={mt.secondary_text_color}
          borderWidth="1px"
          borderStyle="dashed"
          borderColor={mt.border_color}
          borderRadius="6px"
          _hover={{ backgroundColor: hoverBg, borderColor: mt.secondary_text_color }}
          onClick={onAddCategory}
        >
          <LuPlus style={{ width: 13, height: 13 }} />
          <Box fontSize="13px" whiteSpace="nowrap">{t('menus.addCategory')}</Box>
        </Flex>
      )}
    </Flex>
  );
});

export default CategoryTabs;
